<script lang="ts" setup>
import {
  BlockCarouselImageSelectFields,
  BlockCarouselManualFields,
  BlockCoverAnimationFields,
  BlockCoverNavigationFields,
  BlockCoverPaletteFields,
  BlockCoverUPSFields,
  BlockCoverVariantsFields,
  BlockFAQCollectionFields,
  BlockFooterCTAFields,
  BlockFormFields,
  BlockImageCollectionFields,
  BlockImageTextFields,
  BlockImageWithTextCollectionFields,
  BlockProjectStatsFields,
  BlockSimpleSpecificationFields,
  BlockSingleQuoteFields,
  BlockVerticalSliderFields,
} from '~/utils/CmsBlockFields'

const { $directus, $readItems, $readItem } = useNuxtApp()
const { t } = useI18n()
const route = useRoute()
const { dt } = useDirectusTranslation()

const { data, error } = await useAsyncData('blogpost_detail', async () => {
  const projects = await $directus.request($readItems('blogposts', {
    filter: {
      slug: {
        _eq: route.params.slug,
      },
      status: {
        _eq: 'published',
      },
    },
  }))
  if (projects.length === 0) {
    console.error('Blog not found')
    throw createError({
      statusCode: 404,
      message: 'no published blog with that slug found',
    })
  }

  return await $directus.request($readItem('blogposts', projects[0]?.id, {
    fields: [
      'id',
      'slug',
      {
        seo: ['*', {
          translations: ['*'],
        }],
        translations: ['*'],
        hero_image: ['*'],
        hero_video: ['*'],

        blocks: [
          'collection',
          'id',
          {
            item: {
              block_carousel_image_select: BlockCarouselImageSelectFields,
              block_carousel_manual: BlockCarouselManualFields,
              block_footer_cta: BlockFooterCTAFields,
              block_image_collection: BlockImageCollectionFields,
              block_image_text: BlockImageTextFields,
              block_cover_animation: BlockCoverAnimationFields,
              block_cover_navigation: BlockCoverNavigationFields,
              block_cover_palette: BlockCoverPaletteFields,
              block_cover_ups: BlockCoverUPSFields,
              block_cover_variants: BlockCoverVariantsFields,
              block_project_stats: BlockProjectStatsFields,
              block_single_quote: BlockSingleQuoteFields,
              block_faq_collection: BlockFAQCollectionFields,
              block_form: BlockFormFields,
              block_image_with_text_collection: BlockImageWithTextCollectionFields,
              block_simple_specification: BlockSimpleSpecificationFields,
              block_vertical_slider: BlockVerticalSliderFields,
            },
          },
        ],
      },
    ],
  }))
})

if (error.value) {
  throw createError({
    fatal: true,
    statusCode: 404,
    message: 'Blog not found',
  })
}
useDirectusSEO(data.value?.seo)
</script>

<template>
  <div>
    <HeroDetail
      :data="{
        hero_image: data?.hero_image,
        hero_video: data?.hero_video,
        translations: data?.translations,
      } as any
      "
      :back-button="{
        text: t('general.back_to_overview'),
        link: '/blog',
      }"
    />
    <div>
      <div class="relative">
        <!-- Border -->
        <Container class="absolute inset-0 border border-neutral-darkGray" />
        <!-- Gradient over border -->
        <div class="bg-gradient absolute inset-x-0 bottom-0 h-50%" />
        <Container class="relative">
          <p class="mx-auto max-w-6xl py-4rem text-center text-balance text-1.7rem font-title">
            {{ dt(data?.translations)?.description ?? '' }}
          </p>
        </Container>
      </div>
      <div v-for="block in data?.blocks" :key="block.id">
        <BlockCarouselImageSelect v-if="block.collection === 'block_carousel_image_select'" :data="block.item" />
        <BlockCarouselManual v-else-if="block.collection === 'block_carousel_manual'" :data="block.item" />
        <BlockFooterCTA v-else-if="block.collection === 'block_footer_cta'" :data="block.item" />
        <BlockImageCollection v-else-if="block.collection === 'block_image_collection'" :data="block.item" />
        <BlockImageText v-else-if="block.collection === 'block_image_text'" :data="block.item" />
        <BlockPoolAnimation v-else-if="block.collection === 'block_cover_animation'" :data="block.item" />
        <BlockPoolNavigation v-else-if="block.collection === 'block_cover_navigation'" />
        <BlockPoolPalette v-else-if="block.collection === 'block_cover_palette'" :data="block.item" />
        <BlockPoolUPS v-else-if="block.collection === 'block_cover_ups'" :data="block.item" />
        <BlockPoolVariants v-else-if="block.collection === 'block_cover_variants'" :specs="data?.tech_specs!" :data="block.item" />
        <BlockProjectStats v-else-if="block.collection === 'block_project_stats'" :data="block.item" />
        <BlockSingleQuote v-else-if="block.collection === 'block_single_quote'" :data="block.item" />
        <BlockFAQCollection v-else-if="block.collection === 'block_faq_collection'" :data="block.item" />
        <BlockForm v-else-if="block.collection === 'block_form'" :data="block.item" />
        <BlockImageWithTextCollection v-else-if="block.collection === 'block_image_with_text_collection'" :data="block.item" />
        <BlockSimpleSpecification v-else-if="block.collection === 'block_simple_specification'" :data="block.item" />
        <BlockVerticalSlider v-else-if="block.collection === 'block_vertical_slider'" :data="block.item" />
        <BlockQuoteCarousel v-else-if="block.collection === 'block_quote_carousel'" :data="block.item" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.bg-gradient {
  background-image: linear-gradient(to bottom, transparent 0%, #000b1a 50%);
}
</style>
